import axios from 'axios';

/**
 * Cliente de mercado/broker — TODO pasa por NUESTRO backend (investments-api).
 *
 * SEGURIDAD: antes este archivo llamaba a Alpaca DIRECTO con llaves en variables
 * VITE_* que quedaban embebidas en el JavaScript publico (cualquiera con F12 las
 * copiaba). Ahora las llaves viven SOLO en el .env del servidor y el backend
 * resuelve la cuenta Alpaca del usuario autenticado (/api/market/*).
 *
 * Las firmas de las funciones se conservan identicas para no tocar las paginas.
 */
const API = axios.create({
  baseURL: import.meta.env.VITE_INVESTMENTS_API_URL || 'http://localhost:5001',
  headers: { 'Content-Type': 'application/json' },
  timeout: 20000,
});

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Comision escalonada Alpaca (modelo financiero v14).
 * Espejo de backend/utils/pricing.js calculateAlpacaCommission.
 * Tramos (TOTAL al cliente, IVA 19% incluido):
 *   < 12 USD  -> below_min (no opera) · 12-50 -> $0.50 · 50-500 -> $2.00 · 500+ -> 0.3% (tope $30)
 * Devuelve {feeTotal, feeBase, iva, tier, effectivePct, minTradeUsd}.
 */
export const IVA_RATE = 0.19;
export const ALPACA_MIN_TRADE_USD = 12;

export function calculateAlpacaCommission(amountUsd) {
  const minTrade = ALPACA_MIN_TRADE_USD;
  if (!Number.isFinite(amountUsd) || amountUsd < minTrade) {
    return { feeTotal: 0, feeBase: 0, iva: 0, tier: 'below_min', effectivePct: 0, minTradeUsd: minTrade };
  }
  let feeTotal, tier;
  if (amountUsd < 50)        { feeTotal = 0.50; tier = 'tier1'; }
  else if (amountUsd < 500)  { feeTotal = 2.00; tier = 'tier2'; }
  else {
    const pctFee = amountUsd * 0.003; // 0.3%
    if (pctFee > 30) { feeTotal = 30; tier = 'cap'; }
    else             { feeTotal = pctFee; tier = 'tier3'; }
  }
  const feeBase = feeTotal / (1 + IVA_RATE);
  const iva = feeTotal - feeBase;
  const effectivePct = amountUsd > 0 ? (feeTotal / amountUsd) * 100 : 0;
  return { feeTotal, feeBase, iva, tier, effectivePct, minTradeUsd: minTrade };
}

// ===== Datos de mercado (proxy /api/market) =====

export const getSnapshot = async (symbol) => {
  const { data } = await API.get(`/api/market/snapshot/${symbol}`);
  return data;
};

export const getSnapshots = async (symbols) => {
  const { data } = await API.get('/api/market/snapshots', {
    params: { symbols: symbols.join(',') },
  });
  return data;
};

export const getBars = async (symbol, timeframe = '1Day', start, end, limit = 1000) => {
  const params = { timeframe, limit };
  if (start) params.start = start;
  if (end) params.end = end;
  const { data } = await API.get(`/api/market/bars/${symbol}`, { params });
  return data.bars || [];
};

export const getNews = async (symbols, limit = 10) => {
  const { data } = await API.get('/api/market/news', {
    params: { symbols: symbols.join(','), limit },
  });
  return data.news || [];
};

export const getOptionContracts = async (underlying, type = 'call', expirationDate) => {
  const params = { underlying, type };
  if (expirationDate) params.expiration_date = expirationDate;
  const { data } = await API.get('/api/market/options/contracts', { params });
  return data.option_contracts || [];
};

export const getOptionSnapshots = async (underlying) => {
  const { data } = await API.get(`/api/market/options/snapshots/${underlying}`);
  return data.snapshots || {};
};

export const getMostActives = async (top = 10) => {
  const { data } = await API.get('/api/market/screener/most-actives', { params: { top } });
  return data.most_actives || [];
};

export const getMarketMovers = async (top = 10) => {
  const { data } = await API.get('/api/market/screener/movers', { params: { top } });
  return data;
};

// ===== Mapping demo (solo IDs, no son secretos) =====
// NOTA: este valor YA NO decide los datos — el backend resuelve la cuenta del
// usuario por su JWT. Se conserva solo para las cuentas demo explicitas. NUNCA
// debe haber un fallback a la cuenta de otra persona: un usuario sin cuenta -> null.

// Map FlowexPay users to Alpaca sandbox accounts (solo demos curados)
const ALPACA_ACCOUNTS = {
  'ivan@flowexpay.com': 'ea26b2d8-8385-4177-89cc-a2a986561ab7',
  'ivanauli79@gmail.com': 'ea26b2d8-8385-4177-89cc-a2a986561ab7',
  'jasarmientosa@hotmail.com': 'af3773df-dd7a-4c8d-b067-26ab3b7309bf',
  // Demo accounts created for Progresión meeting (May 2026)
  'juanlamboglia@icloud.com': '766d65e8-ca89-4fa7-a957-0eb5dce4e828',
  'ivansarmi@hotmail.com': 'e3c8246e-fe46-4825-a1e3-dc3e87194212',
  'dr.ioanisvalencia@gmail.com': '4fa2b4aa-c692-4125-b174-4f9016bc4da7',
  'johanna_30115@hotmail.com': '53b17457-94c2-44ca-a46e-fe37f9618c42',
};

export const getAlpacaAccountId = (email) => {
  // 1. Prefer the user's own account ID stored after onboarding (real users)
  try {
    const profileStr = localStorage.getItem('fp_investor_profile');
    if (profileStr) {
      const profile = JSON.parse(profileStr);
      if (profile?.alpacaAccountId) return profile.alpacaAccountId;
    }
  } catch {}

  // 2. Solo cuentas demo explicitas. SIN fallback: desconocido -> null (el backend
  //    decide por el JWT; jamas devolvemos la cuenta de otra persona).
  if (!email) return null;
  return ALPACA_ACCOUNTS[email.toLowerCase()] || null;
};

// ===== Broker (el backend resuelve la cuenta del usuario autenticado) =====
// El parametro accountId se conserva por compatibilidad pero YA NO viaja:
// el servidor decide la cuenta segun el JWT (nadie puede consultar otra).

export const getAccount = async (_accountId) => {
  const { data } = await API.get('/api/market/broker/account');
  return data;
};

export const getPositions = async (_accountId) => {
  const { data } = await API.get('/api/market/broker/positions');
  return data;
};

export const getOrders = async (status = 'all', limit = 20, _accountId) => {
  const { data } = await API.get('/api/market/broker/orders', {
    params: { status, limit },
  });
  return data;
};

export const placeStockOrder = async ({ symbol, notional, qty, side = 'buy', type = 'market', timeInForce = 'day' }) => {
  const { data } = await API.post('/api/market/broker/orders', {
    symbol, notional, qty, side, type, timeInForce,
  }, { timeout: 30000 });
  return data;
};

export const placeOptionOrder = async ({ symbol, qty, side = 'buy', type = 'market', timeInForce = 'day', primaUsd }) => {
  // primaUsd va SOLO para que el registro guarde el monto de la operacion. La
  // comision no depende de ella (es fija por contrato), asi que un precio
  // equivocado aqui no puede alterar lo que se le cobra al cliente.
  const { data } = await API.post('/api/market/broker/orders', {
    symbol, qty, side, type, timeInForce, primaUsd,
  }, { timeout: 30000 });
  return data;
};

export const searchAssets = async (query) => {
  // Busqueda por NOMBRE o ticker contra el universo completo de Alpaca (~14k acciones),
  // via nuestro proxy en el backend (las llaves viven server-side).
  try {
    const { data } = await API.get('/api/market/assets/search', { params: { q: query }, timeout: 8000 });
    if (Array.isArray(data) && data.length > 0) {
      return data.map((r) => ({ symbol: r.symbol, name: r.name || r.symbol }));
    }
  } catch { /* cae al fallback de abajo */ }
  // Fallback: match exacto de ticker via snapshot (por si el endpoint aun no esta desplegado).
  try {
    const upper = String(query || '').toUpperCase();
    const data = await getSnapshots([upper]);
    if (data && Object.keys(data).length > 0) {
      return Object.keys(data).map((s) => ({ symbol: s, name: s }));
    }
  } catch { /* noop */ }
  return [];
};

export const periodToTimeframe = (period) => {
  switch (period) {
    case '1D': return { timeframe: '5Min', days: 1 };
    case '1S': return { timeframe: '30Min', days: 7 };
    case '1M': return { timeframe: '1Day', days: 30 };
    case '3M': return { timeframe: '1Day', days: 90 };
    case '1A': return { timeframe: '1Week', days: 365 };
    case 'Max': return { timeframe: '1Month', days: 365 * 5 };
    default: return { timeframe: '1Day', days: 30 };
  }
};

export default API;

/**
 * TARIFAS DE OPCIONES — espejo de investments-api/data/tarifasOpciones.js.
 *
 * La pantalla decia "Comision $0 GRATIS" porque la tarifa vivia solo en el
 * Excel del modelo financiero y nadie que mirara el codigo podia saberla.
 * Ahora esta aqui, y si cambia se cambia en los dos archivos (backend y este).
 *
 * FUENTE: FlowexPay Modelo Financiero.xlsx -> Supuestos -> 5. OPCIONES
 *   Fee por contrato   $1.50  (IVA 19% incluido)
 *   Fee por ejercicio  $7.00  por posicion
 * Costo Alpaca (term sheet, no se muestra al cliente): $0.15/contrato, $5/ejercicio.
 */
export const OPCIONES_FEE_POR_CONTRATO = 1.50;
export const OPCIONES_FEE_POR_EJERCICIO = 7.00;

/** Comision de una operacion de opciones. `contratos` son contratos, no acciones. */
export function comisionOpciones(contratos) {
  const n = Math.max(0, Math.floor(Number(contratos) || 0));
  const feeTotal = Math.round(n * OPCIONES_FEE_POR_CONTRATO * 100) / 100;
  const base = feeTotal / 1.19;
  return {
    contratos: n,
    porContrato: OPCIONES_FEE_POR_CONTRATO,
    feeTotal,
    feeBase: Math.round(base * 100) / 100,
    iva: Math.round((feeTotal - base) * 100) / 100,
  };
}
